import { createApi } from '@reduxjs/toolkit/query/react';
import { request, ClientError } from 'graphql-request';
import config from 'src/config';
import authService from 'src/services/auth-service';
import { JSONValue } from 'src/shared/types';

/** One failure the backend reported: by code where it sends one, by message where it does not. */
export type GraphFailure = {
    code?: string;
    message?: string;
};

/**
 * A failed graph call, as callers see it. `failures` names what went wrong so a caller can act on
 * it without reaching through the response itself. It is a list because a response may carry
 * several errors and the one a caller cares about need not be first, and each stays whole so a
 * code and a message that belong together are not matched across different errors.
 */
export type GraphApiError = {
    status: number | string;
    failures?: GraphFailure[];
    message?: string;
    data?: unknown;
    response?: { errors?: unknown[] };
};

/**
 * Describes each error a failed graph call carried, in the order the backend sent them. The
 * messages are the errors' own, without the request and response blob that ClientError appends
 * to `message`.
 */
export const describeGraphFailures = (errors?: readonly { message?: string; extensions?: Record<string, unknown> }[]): GraphFailure[] =>
    (errors ?? []).map(error => ({
        code: typeof error.extensions?.code === 'string' ? error.extensions.code : undefined,
        message: error.message,
    }));

/**
 * DD-6079: portfolio reads default to Mongo on the backend now. `?source=neo4j`
 * in the URL is the escape hatch back to the old Neo4j path (e.g. to compare
 * against Mongo, or route around a Mongo-path bug); `?source=mongo` stays as
 * an explicit, now-redundant opt-in. Both forward an internal request header
 * the backend's `useMongoDataSource` checks for (`X-Portfolio-Data-Source`).
 * Deliberately opt-in only on these two exact values: every other query keeps
 * working exactly as before.
 */
export const portfolioDataSourceHeader = (search: string): Record<string, string> => {
    const source = new URLSearchParams(search).get('source');
    return source === 'mongo' || source === 'neo4j' ? { 'X-Portfolio-Data-Source': source } : {};
};

const graphApiBaseQuery =
    ({ baseUrl }: { baseUrl: string }) =>
    async ({ body, variables = {} }: { body: string; variables?: JSONValue }) => {
        try {
            const accessToken = await authService.getAccessToken();

            if (!accessToken) {
                // No token available; return 401 quietly and allow route guards to redirect
                return {
                    error: {
                        status: 401,
                        data: 'No access token available',
                        message: 'Authentication required',
                    },
                };
            }

            const headers = {
                authorization: `${accessToken}`,
                ...portfolioDataSourceHeader(window.location.search),
            };

            const result = await request(baseUrl, body, variables, headers);
            return { data: result };
        } catch (error) {
            if (error instanceof ClientError) {
                const failures = describeGraphFailures(error.response.errors);

                // Handle 401 specifically - don't log as error since it's expected user flow
                if (error.response.status === 401) {
                    // Return 401 and let ProtectedRoute handle redirect without forcing signout
                    return {
                        error: {
                            status: 401,
                            failures,
                            data: error.response.data,
                            response: {
                                errors: error.response.errors,
                            },
                            message: 'Authentication required',
                        },
                    };
                }

                // Log other GraphQL errors as they may be actual issues
                console.error('GraphQL error', {
                    status: error.response.status,
                    message: error.message,
                    errors: error.response.errors,
                });

                return {
                    error: {
                        status: error.response.status,
                        failures,
                        data: error.response.data,
                        response: {
                            errors: error.response.errors,
                        },
                        message: error.message,
                    },
                };
            }

            // Handle network errors
            if (error instanceof Error && error.name === 'AbortError') {
                console.info('GraphQL request was aborted');
                return {
                    error: {
                        status: 'FETCH_ERROR',
                        error: 'Request was aborted',
                    },
                };
            }

            // Only log unexpected errors
            console.error('Unexpected GraphQL error:', error);
            return {
                error: {
                    status: 500,
                    meta: { error: (error as Error).message },
                    message: 'An unexpected error occurred',
                },
            };
        }
    };

export default createApi({
    reducerPath: 'graphApi',
    baseQuery: graphApiBaseQuery({
        baseUrl: config.graphApiUrl,
    }),
    endpoints: () => ({}), // will inject endpoints in other files
    refetchOnFocus: true,
    refetchOnReconnect: true,
    tagTypes: ['customer', 'project', 'user', 'users', 'customerAdmin', 'sampling-event', 'site', 'UserCountSummary', 'project-sites'],
});
